import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MapPin, FileText, ChevronDown, MessageCircle } from "lucide-react";
import { NAV_LINKS, waLink } from "@/lib/site";
import { EASE } from "@/lib/motion";
import { WhatsAppButton } from "./WhatsAppButton";

export default function Nav({ onOpenPdf }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const scrolledRef = useRef(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      const next = window.scrollY > 24;
      if (next === scrolledRef.current) return;
      scrolledRef.current = next;
      setScrolled(next);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      window.addEventListener("click", handleOutsideClick);
    }
    return () => window.removeEventListener("click", handleOutsideClick);
  }, [dropdownOpen]);

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.55, ease: EASE, delay: 0.05 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-300 ease-out-expo ${
        scrolled
          ? "backdrop-blur-xl bg-[#FAF6F0]/85 border-b border-[#3B2818]/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 flex items-center justify-between h-[68px] md:h-[76px]">
        {/* Logo area */}
        <div className="flex items-center gap-3">
          <a
            data-testid="brand-logo"
            href="#top"
            className="flex items-baseline gap-2 group"
          >
            <span className="font-serif text-2xl md:text-[26px] leading-none tracking-tight text-[#3B2818]">
              Signature
            </span>
            <span className="font-script text-2xl md:text-3xl -mt-1 text-[#C06A22] leading-none">
              Spoon
            </span>
          </a>
          <span className="hidden xl:inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.14em] text-[#3B2818]/70 bg-[#3B2818]/5 px-2.5 py-1 rounded-full border border-[#3B2818]/10">
            <MapPin className="h-3 w-3 text-[#C06A22]" />
            Pune & Karad
          </span>
        </div>

        {/* Desktop Nav Links */}
        <nav className="hidden md:flex items-center gap-7 lg:gap-9">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              data-testid={`nav-link-${l.href.slice(1)}`}
              href={l.href}
              className="group text-[12px] lg:text-[13px] uppercase tracking-[0.18em] text-[#3B2818]/80 hover:text-[#C06A22] transition-colors duration-200 relative"
            >
              <span>{l.label}</span>
              <span
                aria-hidden
                className="absolute left-0 -bottom-1.5 h-px w-full bg-[#C06A22] [clip-path:inset(0_100%_0_0)] transition-[clip-path] duration-200 ease-out-expo group-hover:[clip-path:inset(0_0_0_0)]"
              />
            </a>
          ))}
        </nav>

        {/* Single Combined Action Dropdown CTA */}
        <div className="flex items-center gap-3">
          <div className="relative hidden sm:block" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen((v) => !v)}
              data-testid="nav-menu-order-cta"
              aria-expanded={dropdownOpen}
              aria-haspopup="true"
              aria-controls="desktop-nav-order-dropdown"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#3B2818] hover:bg-[#C06A22] text-[#FAF6F0] text-xs font-semibold uppercase tracking-[0.14em] transition-all duration-200 shadow-sm active:scale-95 cursor-pointer"
            >
              <span>View Menu / Order</span>
              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform duration-200 ${
                  dropdownOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {/* Dropdown Menu Overlay */}
            <AnimatePresence>
              {dropdownOpen && (
                <motion.div
                  id="desktop-nav-order-dropdown"
                  role="menu"
                  aria-label="View Menu or Order options"
                  initial={{ opacity: 0, y: 8, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.96 }}
                  transition={{ duration: 0.18, ease: "easeOut" }}
                  className="absolute right-0 mt-2 w-60 bg-[#FAF6F0] rounded-2xl shadow-xl border border-[#3B2818]/15 p-2 z-50"
                >
                  {onOpenPdf && (
                    <button
                      role="menuitem"
                      onClick={() => {
                        setDropdownOpen(false);
                        onOpenPdf();
                      }}
                      className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl hover:bg-[#F2EBE0] text-[#3B2818] transition-colors text-left group"
                    >
                      <div className="h-8 w-8 rounded-full bg-[#C06A22]/15 text-[#C06A22] flex items-center justify-center shrink-0 group-hover:bg-[#C06A22] group-hover:text-[#FAF6F0] transition-colors">
                        <FileText className="h-4 w-4" />
                      </div>
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wider text-[#3B2818]">
                          View PDF Menu
                        </div>
                        <div className="text-[10px] text-[#6B5343]">
                          In-browser catalog viewer
                        </div>
                      </div>
                    </button>
                  )}

                  <a
                    role="menuitem"
                    href={waLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setDropdownOpen(false)}
                    className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl hover:bg-[#F2EBE0] text-[#3B2818] transition-colors text-left group mt-1"
                  >
                    <div className="h-8 w-8 rounded-full bg-[#25D366]/15 text-[#25D366] flex items-center justify-center shrink-0 group-hover:bg-[#25D366] group-hover:text-white transition-colors">
                      <MessageCircle className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wider text-[#3B2818]">
                        Order on WhatsApp
                      </div>
                      <div className="text-[10px] text-[#6B5343]">
                        Chat directly to order
                      </div>
                    </div>
                  </a>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Mobile Menu Toggle Button */}
          <button
            data-testid="mobile-menu-toggle"
            aria-label={open ? "Close Navigation Menu" : "Open Navigation Menu"}
            aria-expanded={open}
            aria-controls="mobile-navigation-drawer"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden h-10 w-10 rounded-full border border-[#3B2818]/20 flex items-center justify-center text-[#3B2818] transition-transform duration-150 ease-out active:scale-[0.94]"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-navigation-drawer"
            role="region"
            aria-label="Mobile Navigation Menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-[#FAF6F0] border-t border-[#3B2818]/10 shadow-xl"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-[#C06A22] font-medium pb-2 border-b border-[#3B2818]/10">
                <MapPin className="h-3.5 w-3.5" />
                <span>Serving Pune & Karad — Homemade Bakes</span>
              </div>
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  data-testid={`mobile-nav-link-${l.href.slice(1)}`}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="font-serif text-2xl text-[#3B2818] hover:text-[#C06A22] transition-colors duration-200"
                >
                  {l.label}
                </a>
              ))}

              <div className="pt-3 border-t border-[#3B2818]/10 flex flex-col gap-3">
                {onOpenPdf && (
                  <button
                    onClick={() => {
                      setOpen(false);
                      onOpenPdf();
                    }}
                    data-testid="mobile-nav-pdf-cta"
                    className="flex items-center gap-2.5 font-serif text-xl text-[#C06A22] hover:text-[#3B2818] transition-colors text-left"
                  >
                    <FileText className="h-5 w-5" />
                    <span>View Full Menu PDF</span>
                  </button>
                )}
                <WhatsAppButton
                  size="md"
                  variant="caramel"
                  label={`Order on WhatsApp`}
                  testId="mobile-wa-cta"
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
